import type {
  Challenge,
  ChallengeRef,
  MfaMethod,
  VerificationPurpose,
  VerifiedResult,
} from './types';
import { AuthRequestError } from './response';
import { alternativeMethod, secondsUntil } from './wording';

export type ChallengeAction = 'verify' | 'resend' | 'switch';

export type ChallengeState = {
  challenge: Challenge;
  code: string;
  pending: ChallengeAction | null;
  error: string;
  resendBlocked: boolean;
  focusCode: number;
  exit: string | null;
};

export type ChallengeEvent =
  | { type: 'typed'; value: string }
  | { type: 'started'; action: ChallengeAction }
  | { type: 'sent'; challenge: Challenge }
  | { type: 'failed'; error: unknown };

export const initialChallengeState = (
  challenge: Challenge
): ChallengeState => ({
  challenge,
  code: '',
  pending: null,
  error: '',
  resendBlocked: false,
  focusCode: 0,
  exit: null,
});

export const canSubmitCode = (state: ChallengeState): boolean =>
  state.code.length === 6 && !state.pending && !state.exit;

const failed = (state: ChallengeState, error: unknown): ChallengeState => {
  const message =
    error instanceof Error && error.message
      ? error.message
      : 'Something went wrong';
  const known = error instanceof AuthRequestError ? error : undefined;
  if (known?.code === 'MFA_EXPIRED' || known?.code === 'MFA_LOCKED') {
    return { ...state, pending: null, error: '', exit: message };
  }
  const next = {
    ...state,
    pending: null,
    error: message,
    challenge: known?.resendAvailableAt
      ? { ...state.challenge, resendAvailableAt: known.resendAvailableAt }
      : state.challenge,
  };
  switch (known?.code) {
    case 'MFA_INVALID_CODE':
      return { ...next, code: '', focusCode: state.focusCode + 1 };
    case 'MFA_RESEND_LIMIT':
      return { ...next, resendBlocked: true };
    default:
      return next;
  }
};

export const challengeReducer = (
  state: ChallengeState,
  event: ChallengeEvent
): ChallengeState => {
  switch (event.type) {
    case 'typed':
      return state.pending
        ? state
        : {
            ...state,
            code: event.value.replace(/\D/g, '').slice(0, 6),
            error: '',
          };
    case 'started':
      return state.pending
        ? state
        : { ...state, pending: event.action, error: '' };
    case 'sent':
      return {
        ...state,
        challenge: event.challenge,
        code: '',
        pending: null,
        error: '',
        focusCode: state.focusCode + 1,
      };
    case 'failed':
      return failed(state, event.error);
  }
};

export type VerificationRequests<
  P extends VerificationPurpose,
  R = VerifiedResult[P],
> = {
  verify: (ref: ChallengeRef<P>, code: string) => Promise<R>;
  resend: (ref: ChallengeRef<P>) => Promise<Challenge>;
  switchTo: (ref: ChallengeRef<P>, method: MfaMethod) => Promise<Challenge>;
};

export type VerificationHost<
  P extends VerificationPurpose,
  R = VerifiedResult[P],
> = {
  requests: VerificationRequests<P, R>;
  onVerified: (result: R) => unknown;
  onExit: (message: string) => void;
};

export type VerificationSnapshot = ChallengeState & {
  secondsUntilResend: number;
  alternative: MfaMethod | undefined;
  canSubmit: boolean;
  canResend: boolean;
};

const toSnapshot = (
  state: ChallengeState,
  secondsUntilResend: number
): VerificationSnapshot => ({
  ...state,
  secondsUntilResend,
  alternative: alternativeMethod(state.challenge),
  canSubmit: canSubmitCode(state),
  canResend:
    !state.pending &&
    state.exit === null &&
    !state.resendBlocked &&
    secondsUntilResend === 0,
});

export const createVerificationController = <
  P extends VerificationPurpose,
  R = VerifiedResult[P],
>(
  purpose: P,
  challenge: Challenge,
  host: () => VerificationHost<P, R>
) => {
  const listeners = new Set<() => void>();
  let state = initialChallengeState(challenge);
  let snapshot = toSnapshot(state, secondsUntil(challenge.resendAvailableAt));
  let timer: ReturnType<typeof setInterval> | undefined;

  const syncTimer = () => {
    const ticking = listeners.size > 0 && snapshot.secondsUntilResend > 0;
    if (ticking && !timer) timer = setInterval(() => publish(state), 1000);
    if (!ticking && timer) {
      clearInterval(timer);
      timer = undefined;
    }
  };

  const publish = (next: ChallengeState) => {
    const seconds = secondsUntil(next.challenge.resendAvailableAt);
    const changed = next !== state || seconds !== snapshot.secondsUntilResend;
    const exit = state.exit === null ? next.exit : null;
    state = next;
    if (changed) snapshot = toSnapshot(next, seconds);
    syncTimer();
    if (!changed) return;
    listeners.forEach((listener) => listener());
    if (exit !== null && listeners.size > 0) host().onExit(exit);
  };

  const dispatch = (event: ChallengeEvent) =>
    publish(challengeReducer(state, event));

  const run = async (
    action: ChallengeAction,
    request: (ref: ChallengeRef<P>) => Promise<Challenge | void>
  ) => {
    const ref = { purpose, token: state.challenge.token };
    dispatch({ type: 'started', action });
    try {
      const sent = await request(ref);
      if (sent) dispatch({ type: 'sent', challenge: sent });
    } catch (error) {
      dispatch({ type: 'failed', error });
    }
  };

  return {
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      publish(state);
      return () => {
        listeners.delete(listener);
        syncTimer();
      };
    },
    getSnapshot: (): VerificationSnapshot => snapshot,
    setCode: (value: string) => dispatch({ type: 'typed', value }),
    verify: async () => {
      if (!snapshot.canSubmit) return;
      const { code } = state;
      await run('verify', async (ref) => {
        const { requests, onVerified } = host();
        await onVerified(await requests.verify(ref, code));
      });
    },
    resend: async () => {
      if (!snapshot.canResend) return;
      await run('resend', (ref) => host().requests.resend(ref));
    },
    switchMethod: async () => {
      const { alternative } = snapshot;
      if (!alternative || state.pending || state.exit !== null) return;
      await run('switch', (ref) => host().requests.switchTo(ref, alternative));
    },
  };
};
